import { useState, type ReactNode } from 'react';
import moment from 'moment';
import { Button } from 'src/components/ui/button';
import { Calendar, type DateRangeValue } from 'src/components/ui/calendar';
import { Checkbox } from 'src/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import { CircleDotIcon } from 'src/assets/icons/circleDotIcon';
import { ISO_DATE_FORMAT } from 'src/shared/constants';
import {
  ESCALATION_REASONS,
  type ChatsRequest,
  type ChatsRequestChange,
} from 'src/pages/ConversationsPage/chatsRequest';

const STATUSES = [
  { value: 'resolved', label: 'Resolved', chatStatuses: ['closed', 'auto_closed'] },
  { value: 'escalated', label: 'Escalated', chatStatuses: ['escalated'] },
  { value: 'in_progress', label: 'In Progress', chatStatuses: ['open'] },
];

const CONVERSATION_TYPES = [
  { value: 'playground', label: 'Playground' },
  { value: 'chat', label: 'Customer chats' },
];

// 'all' sends no dates; the others but 'custom' cover this many days up to today.
type TimePreset = 'today' | 'last_7_days' | 'last_30_days' | 'custom' | 'all';

const TIME_PRESETS: { value: TimePreset; label: string; days?: number }[] = [
  { value: 'today', label: 'Today', days: 1 },
  { value: 'last_7_days', label: 'Last 7 days', days: 7 },
  { value: 'last_30_days', label: 'Last 30 days', days: 30 },
  { value: 'custom', label: 'Custom' },
  { value: 'all', label: 'All time' },
];

// The date field the time range filters on; the list is ordered newest first by it too.
type DateField = 'created_at' | 'updated_at';

type DateRange = { from: Date; to: Date };

interface FilterState {
  statuses: string[];
  escalationReasons: string[];
  types: string[];
  timePreset: TimePreset;
  dateField: DateField;
  customRange: DateRange;
}

const lastDays = (days: number): DateRange => ({
  from: moment()
    .subtract(days - 1, 'days')
    .startOf('day')
    .toDate(),
  to: moment().endOf('day').toDate(),
});

const presetDays = (preset: TimePreset) => TIME_PRESETS.find(({ value }) => value === preset).days;

// The preset whose range the dates match, or 'custom'.
const toTimePreset = (range: DateRange): TimePreset => {
  const preset = TIME_PRESETS.find(
    ({ days }) =>
      days &&
      moment(range.from).isSame(lastDays(days).from, 'day') &&
      moment(range.to).isSame(moment(), 'day'),
  );

  return preset ? preset.value : 'custom';
};

// The form as the current request has it; nothing narrowed when there is no request yet.
const toFilterState = (request: ChatsRequest | null): FilterState => {
  const dateField: DateField = request?.order_by.includes('-created_at')
    ? 'created_at'
    : 'updated_at';
  const start = request?.[`${dateField}_start`];
  const range = start
    ? {
        from: moment(start).startOf('day').toDate(),
        to: moment(request[`${dateField}_end`]).endOf('day').toDate(),
      }
    : lastDays(1);
  const reasons = request?.escalation_reason_category ?? [];

  return {
    statuses: request
      ? STATUSES.filter(({ chatStatuses }) => request.chat_status.includes(chatStatuses[0])).map(
          ({ value }) => value,
        )
      : STATUSES.map(({ value }) => value),
    escalationReasons: reasons.length ? reasons : ESCALATION_REASONS.map(({ value }) => value),
    types: request ? request.types : ['chat'],
    timePreset: start ? toTimePreset(range) : 'all',
    dateField,
    customRange: range,
  };
};

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <p className="text-tiny leading-normal font-semibold uppercase tracking-[1px] text-ebony">
    {children}
  </p>
);

export const ConversationFilter = ({
  request,
  defaultRequest = null,
  onChange = () => {},
}: {
  request: ChatsRequest | null;
  // What Clear All resets the form to.
  defaultRequest?: ChatsRequest | null;
  onChange?: (changes: ChatsRequestChange) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(() => toFilterState(request));
  const [calendarMonth, setCalendarMonth] = useState<Date>();

  const isEscalatedSelected = filter.statuses.includes('escalated');
  const range =
    filter.timePreset === 'custom'
      ? filter.customRange
      : lastDays(presetDays(filter.timePreset) ?? 1);
  const canApply =
    filter.statuses.length > 0 &&
    filter.types.length > 0 &&
    (!isEscalatedSelected || filter.escalationReasons.length > 0) &&
    (filter.timePreset !== 'custom' || (!!filter.customRange.from && !!filter.customRange.to));

  const update = (changes: Partial<FilterState>) => setFilter({ ...filter, ...changes });

  // A day-based preset also becomes the custom range, so Custom opens on those dates.
  const onTimePresetChange = (timePreset: TimePreset) => {
    if (!timePreset) return;

    const days = presetDays(timePreset);

    if (!days) {
      update({ timePreset });
      return;
    }

    const customRange = lastDays(days);
    update({ timePreset, customRange });
    setCalendarMonth(moment(customRange.to).subtract(1, 'months').toDate());
  };

  // Every open starts from the applied request, so closing without Apply discards the edits.
  const onOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      const state = toFilterState(request);
      setFilter(state);
      setCalendarMonth(moment(state.customRange.to).subtract(1, 'months').toDate());
    }

    setOpen(isOpen);
  };

  const onRangeSelect = (selected: DateRangeValue | undefined) => {
    update({
      customRange: {
        from: selected?.from ? moment(selected?.from).startOf('day').toDate() : null,
        to: selected?.to ? moment(selected?.to).endOf('day').toDate() : null,
      },
    });
  };

  const apply = () => {
    const allReasons = filter.escalationReasons.length === ESCALATION_REASONS.length;
    const data: ChatsRequestChange = {
      chat_status: STATUSES.filter(({ value }) => filter.statuses.includes(value)).flatMap(
        ({ chatStatuses }) => chatStatuses,
      ),
      // Every reason is the same as no reason filter, which also keeps the other statuses' chats.
      escalation_reason_category:
        isEscalatedSelected && !allReasons ? filter.escalationReasons : [],
      types: CONVERSATION_TYPES.filter(({ value }) => filter.types.includes(value)).map(
        ({ value }) => value,
      ),
      created_at_start: '',
      created_at_end: '',
      updated_at_start: '',
      updated_at_end: '',
      order_by: [`-${filter.dateField}`],
    };

    if (filter.timePreset !== 'all') {
      data[`${filter.dateField}_start`] = moment(range.from).startOf('day').format(ISO_DATE_FORMAT);
      data[`${filter.dateField}_end`] = moment(range.to).endOf('day').format(ISO_DATE_FORMAT);
    }

    onChange(data);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="shadow-search gap-2">
          Filters
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M11.805 0H0.180011C0.130937 0.00420664 0.0851092 0.0288781 0.051724 0.0690641C0.0183387 0.10925 -0.000134235 0.161977 1.15866e-05 0.216667V0.933333C-0.000439034 1.00679 0.0122585 1.07961 0.0373659 1.14757C0.0624732 1.21552 0.0994893 1.27725 0.146261 1.32917L4.64626 6.32917V10.4958L7.365 12V6.32083L11.865 1.32083C11.9511 1.21765 11.9993 1.08237 12 0.941667V0.216667C12 0.159203 11.9795 0.104093 11.9429 0.0634603C11.9063 0.0228274 11.8567 0 11.805 0Z"
              fill="#05050D"
              fillOpacity="0.8"
            />
          </svg>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Filters"
        className="flex w-[528px] max-w-[calc(100vw-2rem)] max-h-[var(--radix-popover-content-available-height)] flex-col gap-3 overflow-y-auto rounded-lg border-border_subtle bg-white p-5 font-jamjuree shadow-[0_2px_1px_rgba(0,0,0,0.25)]"
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm leading-normal font-bold text-ebony_opacity_80">Filters</h2>
          <Button
            variant="ghost-grey"
            className="h-auto px-2 py-1 text-xs"
            onClick={() => setFilter(toFilterState(defaultRequest))}
          >
            Clear All
          </Button>
        </div>
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-2">
            <SectionLabel>Status</SectionLabel>
            <ToggleGroup
              type="multiple"
              aria-label="Status"
              value={filter.statuses}
              onValueChange={(statuses) => update({ statuses })}
              className="flex flex-wrap gap-1"
            >
              {STATUSES.map(({ value, label }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  variant="chip"
                  className="group rounded-full px-3 py-1.5 text-sm leading-normal"
                >
                  {label}
                  <CircleDotIcon className="text-grey_300 group-data-[state=on]:text-purple_600" />
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {isEscalatedSelected && (
              <fieldset
                aria-labelledby="escalation-reason-label"
                className="flex min-w-0 flex-col gap-2 rounded-lg border border-grey_300 p-4"
              >
                <p
                  id="escalation-reason-label"
                  className="text-xs leading-normal font-semibold text-grey_750"
                >
                  Escalation reason
                </p>
                <div className="flex flex-wrap gap-2">
                  {ESCALATION_REASONS.map(({ value, label }) => (
                    <label
                      key={value}
                      className="flex cursor-pointer items-center gap-2 text-base-xs leading-normal font-medium text-tertiary"
                    >
                      <Checkbox
                        checked={filter.escalationReasons.includes(value)}
                        onCheckedChange={(checked) =>
                          update({
                            escalationReasons: checked
                              ? [...filter.escalationReasons, value]
                              : filter.escalationReasons.filter((reason) => reason !== value),
                          })
                        }
                        className="rounded border-purple_600 data-[state=checked]:bg-purple_600 data-[state=checked]:text-white"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <SectionLabel>Conversations</SectionLabel>
            <ToggleGroup
              type="multiple"
              aria-label="Conversations"
              value={filter.types}
              onValueChange={(types) => update({ types })}
              className="flex flex-wrap gap-1"
            >
              {CONVERSATION_TYPES.map(({ value, label }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  variant="chip"
                  className="group rounded-full px-3 py-1.5 text-sm leading-normal"
                >
                  {label}
                  <CircleDotIcon className="text-grey_300 group-data-[state=on]:text-purple_600" />
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </section>

          <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
              <SectionLabel>Time range</SectionLabel>
              <ToggleGroup
                type="single"
                aria-label="Filter by date"
                value={filter.dateField}
                onValueChange={(dateField: DateField) => dateField && update({ dateField })}
                className="flex"
              >
                <ToggleGroupItem value="created_at" variant="segment">
                  Created
                </ToggleGroupItem>
                <ToggleGroupItem value="updated_at" variant="segment">
                  Last updated
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <ToggleGroup
              type="single"
              aria-label="Time range"
              value={filter.timePreset}
              onValueChange={onTimePresetChange}
              className="flex flex-wrap gap-1"
            >
              {TIME_PRESETS.map(({ value, label }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  variant="chip"
                  className="rounded-lg px-2 py-1 text-base-xs leading-normal"
                >
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {filter.timePreset === 'custom' && (
              <Calendar
                variant="filter"
                mode="range"
                numberOfMonths={2}
                resetOnSelect={true}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                disabled={{ after: new Date() }}
                selected={filter.customRange}
                onSelect={onRangeSelect}
              />
            )}
            {filter.timePreset !== 'all' && (
              <p className="text-xs leading-normal font-medium text-grey_450">
                {moment(range.from).isValid() ? moment(range.from).format('ll') : 'Start date'} –{' '}
                {moment(range.to).isValid() ? moment(range.to).format('ll') : 'End date'} · by{' '}
                {filter.dateField === 'created_at' ? 'created' : 'last updated'} date
              </p>
            )}
          </section>

          <div className="flex justify-center gap-2">
            <Button variant="tertiary" className="w-[100px]" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="dark" className="w-[100px]" onClick={apply} disabled={!canApply}>
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
